import React, { useEffect, useMemo, useState, FC } from 'react';

import { getState } from '@client/state/reducer';
import { ConsentStatus, PageType } from '@schibsted-nmp/advertising-shared';

const Tooltip = ({ text }) => {
  const [visible, setVisible] = useState(false); // default is set to false
  return (
    <div className="relative">
      {visible && (
        <div className="absolute left-20 bottom-20 border-2 rounded-4 p-4 bg-white whitespace-nowrap">
          {text}
        </div>
      )}
      <div
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <path d="M12 16L12 12"></path>
          <path d="M12 8L12.01 8"></path>
        </svg>
      </div>
    </div>
  );
};

interface CheckboxProps {
  id: string;
  checked: boolean;
  onChange: () => void;
  tooltip?: string;
  children: string | React.ReactNode;
}

const Checkbox: FC<CheckboxProps> = ({
  children,
  id,
  checked,
  onChange,
  tooltip = ''
}) => (
  <div className="flex justify-between items-center">
    <label className="flex" htmlFor={id}>
      {children} {tooltip ? <Tooltip text={tooltip} /> : null}
    </label>
    <input id={id} type="checkbox" checked={checked} onChange={onChange} />
  </div>
);

const defaultAdId = '21306';

const DevPanel = () => {
  const { config, initialState } = getState();
  const placements = config?.xandr?.placements;
  const url = new URL(window.location.href);
  const [adId, setAdId] = useState(
    url.searchParams.get('finnKode') || defaultAdId
  );
  const [hideDongle, setHideDongle] = useState(
    Boolean(url.searchParams.get('hide_ast_dongle') === 'true')
  );
  const pageType = url.searchParams.get('pageType');
  const [hidePanel, setHidePanel] = useState(false);
  const consent = initialState?.config.consent;
  const errorList = useMemo(() => {
    const errorMessage = initialState?.error;
    return [
      errorMessage ? `Server error message: ${errorMessage}` : null,
      placements?.length === 0
        ? "No advertising loaded because server didn't return any placement"
        : null
    ].filter((e) => !!e);
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
        #appnexus_debug_window {
          display: ${hideDongle ? 'none' : 'block'};
        }
      `;
    document.head.appendChild(style);
  }, [hideDongle]);

  useEffect(() => {
    const sP = url.searchParams;
    const _pageType = sP.get('pageType');
    const _finnKode = sP.get('finnKode');
    if (_pageType === 'object' && !_finnKode) {
      toggleSearchParamAndReload('finnKode', defaultAdId, { noDelete: true });
    }
  }, []);

  const updatePageType = (newPageType: PageType) => {
    if (newPageType === 'object') {
      toggleSearchParamAndReload('finnKode', defaultAdId, { noDelete: true });
    }
    // update query param "pageType" in url and refresh page
    toggleSearchParamAndReload('pageType', newPageType, { noDelete: true });
  };

  const toggleSearchParamAndReload = (
    key: string,
    value: string,
    options: {
      noDelete?: boolean;
    } = {}
  ) => {
    if (!options.noDelete && url.searchParams.get(key)) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
    window.location.href = url.href;
  };

  const handleHideDongle = () => {
    const dongleBox = document.getElementById('appnexus_debug_window');
    if (dongleBox) {
      setHideDongle(!hideDongle);
      toggleSearchParamAndReload('hide_ast_dongle', 'true');
    }
  };

  const showPlacement = (placementId: string) => {
    const el = document.getElementById(placementId);
    if (el) {
      console.log(`Found ${placementId}. Scrolling to it!`);
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      console.log(
        `No placement with id ${placementId}. Could it be a NO BID or another error?`
      );
    }
  };

  return (
    <div className="fixed top-0 left-0 border border-2 flex flex-col p-2 gap-8 s-bg z-30 overflow-y-auto max-h-screen">
      <button
        className="text-12"
        style={{ width: 40 }}
        onClick={() => setHidePanel(!hidePanel)}
      >
        {hidePanel ? 'Show' : 'Hide'}
      </button>

      {hidePanel ? null : (
        <>
          <div className="flex flex-col gap-4">
            <div>
              Member: {initialState?.config?.xandr.pageOpts.member}
              <Tooltip text="Xandr test ID: 10390 and production ID: 10199" />
            </div>
            <div>Brand: {initialState?.config?.brand}</div>
            <div>Device: {initialState?.config?.deviceType}</div>
          </div>
          <div className="flex justify-between items-center">
            <label htmlFor="pageType">Pagetype</label>
            <select
              value={`${pageType}`}
              id="pageType"
              onChange={(e) => updatePageType(e.target.value as PageType)}
            >
              <option value="front">Front</option>
              <option value="result">Result</option>
              <option value="object">Object</option>
            </select>
          </div>
          {pageType === 'object' && (
            <div className="flex flex-row gap-4 justify-between">
              <input
                className="rounded-4 border border-2 p-2"
                style={{ width: 120 }}
                placeholder="Object page Ad ID"
                value={adId}
                onChange={(e) => {
                  setAdId(e.target.value);
                }}
              />
              <button
                className="h-full rounded-2 w-16 p-2"
                onClick={() =>
                  toggleSearchParamAndReload('finnKode', adId, {
                    noDelete: true
                  })
                }
              >
                u
              </button>
            </div>
          )}
          <Checkbox
            id="ast_debug"
            checked={Boolean(url.searchParams.get('ast_debug'))}
            onChange={() => toggleSearchParamAndReload('ast_debug', 'true')}
            tooltip="Sets AST debug mode to true. In debug mode, developer console output is available."
          >
            AST Debug
          </Checkbox>
          <Checkbox
            id="advt_debug"
            checked={Boolean(url.searchParams.get('advt_debug'))}
            onChange={() => toggleSearchParamAndReload('advt_debug', 'true')}
            tooltip="Sets ADVT debub mode to true. In debug mode, developer console output is available."
          >
            ADVT Debug
          </Checkbox>
          <Checkbox
            id="ast_dongle"
            checked={Boolean(url.searchParams.get('ast_dongle'))}
            onChange={() => toggleSearchParamAndReload('ast_dongle', 'yellow')}
          >
            AST Dongle
          </Checkbox>
          <Checkbox
            id="hide_dongle"
            checked={hideDongle}
            onChange={handleHideDongle}
          >
            Hide Dongle Box
          </Checkbox>
          {!!errorList?.length && <h5>Errors:</h5>}
          <ul className="text-14 flex flex-col gap-4" style={{ maxWidth: 200 }}>
            {errorList.map((errorMsg) => (
              <li key={errorMsg} className="s-bg-primary p-4 break-all">
                {errorMsg}
              </li>
            ))}
          </ul>
          {!!placements?.length && <h5>Placements:</h5>}
          <ul className="flex flex-col gap-2">
            {placements?.map((placement) => (
              <li
                key={placement?.placementId}
                id={`${placement?.placementId}-local-entry`}
                onClick={() => showPlacement(placement?.placementId)}
                className="cursor-pointer select-none rounded-4 s-bg-positive-subtle-active p-4 break-all"
              >
                {placement?.placementId}
              </li>
            ))}
          </ul>
          <div className="flex justify-between items-center mb-2">
            <h5 className="mb-0">Consent for advertising:</h5>
          </div>
          {consent === ConsentStatus.accepted && (
            <p className="text-center s-bg-positive-subtle">Accepted</p>
          )}
          {consent === ConsentStatus.rejected && (
            <p className="text-center s-bg-warning-subtle">Rejected</p>
          )}
        </>
      )}
    </div>
  );
};

export default DevPanel;
